import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react'

import { useI18n } from '@tribeplatform/react-components/i18n'

import { useToggyModerateBannerEnabled } from '../../../../featureFlags/hooks/useToggyModerateBannerEnabled.js'

import {
  dismissToggyBanner,
  isToggyBannerDismissed,
  TOGGY_BANNER_HREF,
  toggyBannerHeightRootCss,
} from './session.js'

const LogoMotion = lazy(() => import('./LogoMotion.js'))

const LOGO_MIN_WIDTH_QUERY = '(min-width: 640px)'

const ArrowRightIcon = () => (
  <svg
    className="tmb__cta-arrow"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </svg>
)

const CloseIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
)

const LivingGradient = () => (
  <div className="tmb__bg" aria-hidden>
    <div className="tmb__aurora" />
    <div className="tmb__orb tmb__orb--1" />
    <div className="tmb__orb tmb__orb--2" />
    <div className="tmb__orb tmb__orb--3" />
    <div className="tmb__orb tmb__orb--4" />
    <div className="tmb__orb tmb__orb--5" />
    <div className="tmb__sweep" />
    <div className="tmb__grain" />
    <div className="tmb__veil" />
  </div>
)

const useMatchMedia = (query: string) => {
  const [matches, setMatches] = useState(false)

  useEffect(() => {
    const media = window.matchMedia(query)
    const onChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches)
    }

    setMatches(media.matches)
    media.addEventListener('change', onChange)

    return () => {
      media.removeEventListener('change', onChange)
    }
  }, [query])

  return matches
}

export const ToggyModerateStripBanner = () => {
  const enabled = useToggyModerateBannerEnabled()
  const { $t } = useI18n()
  // Reserve the strip on SSR and first paint so the common (non-dismissed)
  // case never inserts 48px after hydration. sessionStorage is client-only,
  // so dismissed viewers collapse after layout — the rare case.
  const [visible, setVisible] = useState(true)
  const reducedMotion = useMatchMedia('(prefers-reduced-motion: reduce)')
  const showLogo = useMatchMedia(LOGO_MIN_WIDTH_QUERY)

  useLayoutEffect(() => {
    if (isToggyBannerDismissed()) {
      setVisible(false)
    }
  }, [])

  const handleDismiss = useCallback(() => {
    dismissToggyBanner()
    setVisible(false)
  }, [])

  // Flag hook stays above this return so hook order is stable. Off → no
  // strip and no --toggy-banner-height reserve.
  if (!enabled || !visible) {
    return null
  }

  const productName = $t({
    defaultMessage: 'Toggy Moderate',
    id: 'Admin.TopNavBar.ToggyBanner.Name',
  })

  return (
    <>
      {/* eslint-disable-next-line react/no-danger */}
      <style dangerouslySetInnerHTML={{ __html: toggyBannerHeightRootCss() }} />
      <div className="tmb" role="region" aria-label={productName}>
        <LivingGradient />

        {showLogo && !reducedMotion && (
          <div className="tmb__logo">
            <div className="tmb__logo-glow" aria-hidden />
            <Suspense fallback={null}>
              <LogoMotion state="idle" size={28} />
            </Suspense>
          </div>
        )}

        <span className="tmb__badge">
          {$t({
            defaultMessage: 'New',
            id: 'Generics.New',
          })}
        </span>

        <div className="tmb__text">
          <span className="tmb__name">{productName}</span>
          <span className="tmb__message">
            <span className="tmb__headline">
              {$t({
                defaultMessage:
                  'Add AI moderation that knows your rules and shows its work.',
                id: 'Admin.TopNavBar.ToggyBanner.Headline',
              })}
            </span>
            <span className="tmb__supporting">
              {$t({
                defaultMessage:
                  'Every post reviewed in real time, every decision explained, every call yours to overturn.',
                id: 'Admin.TopNavBar.ToggyBanner.Supporting',
              })}
            </span>
          </span>
        </div>

        <a
          className="tmb__cta"
          href={TOGGY_BANNER_HREF}
          target="_blank"
          rel="noopener noreferrer"
        >
          {$t({
            defaultMessage: 'Learn more',
            id: 'Generics.LearnMore',
          })}
          <ArrowRightIcon />
        </a>

        <button
          type="button"
          className="tmb__dismiss"
          onClick={handleDismiss}
          aria-label={$t({
            defaultMessage: 'Dismiss Toggy Moderate banner',
            id: 'Admin.TopNavBar.ToggyBanner.Dismiss',
          })}
        >
          <CloseIcon />
        </button>
      </div>
    </>
  )
}
